// src/hooks/useSoulFile.ts
// Hook for managing the user's Soul File - their transformation profile

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/browserClient';
import { useAuth } from './useAuth';
import type { 
  SoulFile, 
  UserEssence, 
  Manifestation, 
  MystaraNotes,
  TransformationJourney,
  Evidence,
  JourneyStats,
  TransformationPhase,
  Breakthrough
} from '../types/mystara';

interface UseSoulFileReturn {
  soulFile: SoulFile | null;
  loading: boolean;
  error: string | null;
  
  // Update functions
  updateEssence: (updates: Partial<UserEssence>) => Promise<void>;
  updatePhase: (phase: TransformationPhase, progress?: number) => Promise<void>;
  addManifestation: (manifestation: Omit<Manifestation, 'id' | 'createdAt' | 'updatedAt' | 'evidenceCount'>) => Promise<void>;
  updateManifestation: (id: string, updates: Partial<Manifestation>) => Promise<void>;
  addEvidence: (evidence: Omit<Evidence, 'id' | 'createdAt'>) => Promise<void>;
  addBreakthrough: (description: string, isHighlight?: boolean) => Promise<void>;
  addIdentityStatement: (statement: string) => Promise<void>;
  updateMystaraNotes: (updates: Partial<MystaraNotes>) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  
  // Refresh
  refresh: () => Promise<void>;
}

export function useSoulFile(): UseSoulFileReturn {
  const { user } = useAuth();
  const [soulFile, setSoulFile] = useState<SoulFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all soul file data
  const fetchSoulFile = useCallback(async () => {
    if (!user?.id) {
      setSoulFile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch all related data in parallel
      const [
        soulFileRes,
        essenceRes,
        manifestationsRes,
        notesRes,
        breakthroughsRes,
        identityRes,
        evidenceRes,
        streaksRes,
        milestonesRes
      ] = await Promise.all([
        supabase.from('soul_files').select('*').eq('user_id', user.id).single(),
        supabase.from('user_essence').select('*').eq('user_id', user.id).single(),
        supabase.from('manifestations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('mystara_notes').select('*').eq('user_id', user.id).single(),
        supabase.from('breakthroughs').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('identity_statements').select('*').eq('user_id', user.id).eq('is_active', true),
        supabase.from('evidence').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('user_streaks').select('*').eq('user_id', user.id).single(),
        supabase.from('milestones').select('*').eq('user_id', user.id)
      ]);

      // Count conversations and other stats
      const conversationCount = await supabase
        .from('conversations')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id);

      const cardPullCount = await supabase
        .from('card_pulls')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id);

      const ritualCount = await supabase
        .from('daily_alignments')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .or('morning_completed.eq.true,evening_completed.eq.true');

      // Build the soul file object
      const essence: UserEssence = essenceRes.data ? {
        displayName: essenceRes.data.display_name || '',
        preferredName: essenceRes.data.preferred_name || '',
        spark: essenceRes.data.spark,
        coreValues: essenceRes.data.core_values || [],
        gifts: essenceRes.data.gifts || [],
        growthEdges: essenceRes.data.growth_edges || [],
        communicationStyle: essenceRes.data.communication_style || 'balanced',
        updatedAt: essenceRes.data.updated_at
      } : {
        displayName: '',
        preferredName: '',
        spark: null,
        coreValues: [],
        gifts: [],
        growthEdges: [],
        communicationStyle: 'balanced',
        updatedAt: new Date().toISOString()
      };

      const manifestations: Manifestation[] = (manifestationsRes.data || []).map(m => ({
        id: m.id,
        title: m.title,
        description: m.description || '',
        category: m.category,
        feelingUnderneath: m.feeling_underneath || '',
        status: m.status,
        identityStatement: m.identity_statement,
        evidenceCount: 0, // Will be calculated
        createdAt: m.created_at,
        updatedAt: m.updated_at,
        manifestedAt: m.manifested_at
      }));

      const mystaraNotes: MystaraNotes = notesRes.data ? {
        observedPatterns: notesRes.data.observed_patterns || [],
        lightUpTopics: notesRes.data.light_up_topics || [],
        handleGently: notesRes.data.handle_gently || [],
        superpowers: notesRes.data.superpowers || [],
        followUpReminders: [], // Would need separate fetch
        lastUpdated: notesRes.data.updated_at
      } : {
        observedPatterns: [],
        lightUpTopics: [],
        handleGently: [],
        superpowers: [],
        followUpReminders: [],
        lastUpdated: new Date().toISOString()
      };

      const breakthroughs: Breakthrough[] = (breakthroughsRes.data || []).map(b => ({
        id: b.id,
        description: b.description,
        phase: b.phase,
        createdAt: b.created_at,
        isHighlight: b.is_highlight
      }));

      const identityStatements: string[] = (identityRes.data || []).map(i => i.statement);

      const evidence: Evidence[] = (evidenceRes.data || []).map(e => ({
        id: e.id,
        category: e.category,
        description: e.description,
        manifestationId: e.manifestation_id,
        isHighlight: e.is_highlight,
        createdAt: e.created_at
      }));

      const journey: TransformationJourney = {
        currentPhase: soulFileRes.data?.current_phase || 'illuminate',
        phaseStartedAt: soulFileRes.data?.phase_started_at || new Date().toISOString(),
        phaseProgress: soulFileRes.data?.phase_progress || 0,
        keyBreakthroughs: breakthroughs,
        identityStatements,
        oldStories: [], // Would need separate table
        totalDaysActive: streaksRes.data?.total_days_active || 0,
        currentStreak: streaksRes.data?.current_streak || 0,
        longestStreak: streaksRes.data?.longest_streak || 0
      };

      const stats: JourneyStats = {
        totalDaysWithMystara: streaksRes.data?.total_days_active || 0,
        currentStreak: streaksRes.data?.current_streak || 0,
        longestStreak: streaksRes.data?.longest_streak || 0,
        ritualsCompleted: ritualCount.count || 0,
        cardPulls: cardPullCount.count || 0,
        totalConversations: conversationCount.count || 0,
        totalEvidence: evidence.length,
        breakthroughCount: breakthroughs.length,
        milestonesAchieved: (milestonesRes.data || []).map(m => m.milestone_key)
      };

      const completeSoulFile: SoulFile = {
        id: soulFileRes.data?.id || '',
        oderId: user.id,
        essence,
        manifestations,
        dreamLifeSnapshot: soulFileRes.data?.dream_life_snapshot || null,
        journey,
        mystaraNotes,
        evidence,
        stats,
        onboardingCompleted: soulFileRes.data?.onboarding_completed || false,
        mirrorSessionCompletedAt: soulFileRes.data?.mirror_session_completed_at,
        createdAt: soulFileRes.data?.created_at || new Date().toISOString(),
        updatedAt: soulFileRes.data?.updated_at || new Date().toISOString()
      };

      setSoulFile(completeSoulFile);
    } catch (err: any) {
      console.error('[useSoulFile] Error fetching:', err);
      setError(err.message || 'Failed to load soul file');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    fetchSoulFile();
  }, [fetchSoulFile]);

  // Update essence
  const updateEssence = useCallback(async (updates: Partial<UserEssence>) => {
    if (!user?.id) return;

    const dbUpdates: Record<string, any> = {};
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.preferredName !== undefined) dbUpdates.preferred_name = updates.preferredName;
    if (updates.spark !== undefined) dbUpdates.spark = updates.spark;
    if (updates.coreValues !== undefined) dbUpdates.core_values = updates.coreValues;
    if (updates.gifts !== undefined) dbUpdates.gifts = updates.gifts;
    if (updates.growthEdges !== undefined) dbUpdates.growth_edges = updates.growthEdges;
    if (updates.communicationStyle !== undefined) dbUpdates.communication_style = updates.communicationStyle;

    const { error } = await supabase
      .from('user_essence')
      .update(dbUpdates)
      .eq('user_id', user.id);

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Update phase
  const updatePhase = useCallback(async (phase: TransformationPhase, progress?: number) => {
    if (!user?.id) return;

    const updates: Record<string, any> = {
      current_phase: phase,
      phase_started_at: new Date().toISOString()
    };
    if (progress !== undefined) updates.phase_progress = progress;

    const { error } = await supabase
      .from('soul_files')
      .update(updates)
      .eq('user_id', user.id);

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Add manifestation
  const addManifestation = useCallback(async (
    manifestation: Omit<Manifestation, 'id' | 'createdAt' | 'updatedAt' | 'evidenceCount'>
  ) => {
    if (!user?.id) return;

    const { error } = await supabase.from('manifestations').insert({
      user_id: user.id,
      title: manifestation.title,
      description: manifestation.description,
      category: manifestation.category,
      feeling_underneath: manifestation.feelingUnderneath,
      status: manifestation.status,
      identity_statement: manifestation.identityStatement
    });

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Update manifestation
  const updateManifestation = useCallback(async (id: string, updates: Partial<Manifestation>) => {
    if (!user?.id) return;

    const dbUpdates: Record<string, any> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.feelingUnderneath !== undefined) dbUpdates.feeling_underneath = updates.feelingUnderneath;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.identityStatement !== undefined) dbUpdates.identity_statement = updates.identityStatement;
    if (updates.status === 'manifested') dbUpdates.manifested_at = new Date().toISOString();

    const { error } = await supabase
      .from('manifestations')
      .update(dbUpdates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Add evidence
  const addEvidence = useCallback(async (evidence: Omit<Evidence, 'id' | 'createdAt'>) => {
    if (!user?.id) return;

    const { error } = await supabase.from('evidence').insert({
      user_id: user.id,
      category: evidence.category,
      description: evidence.description,
      manifestation_id: evidence.manifestationId,
      is_highlight: evidence.isHighlight
    });

    if (error) throw error;

    // Log activity for streak
    await supabase.from('activity_log').insert({
      user_id: user.id,
      activity_type: 'evidence-logged',
      metadata: { category: evidence.category }
    });

    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Add breakthrough
  const addBreakthrough = useCallback(async (description: string, isHighlight = false) => {
    if (!user?.id || !soulFile) return;

    const { error } = await supabase.from('breakthroughs').insert({
      user_id: user.id,
      description,
      phase: soulFile.journey.currentPhase,
      is_highlight: isHighlight
    });

    if (error) throw error;

    // Log activity
    await supabase.from('activity_log').insert({
      user_id: user.id,
      activity_type: 'breakthrough',
      metadata: { description }
    });

    await fetchSoulFile();
  }, [user?.id, soulFile, fetchSoulFile]);

  // Add identity statement
  const addIdentityStatement = useCallback(async (statement: string) => {
    if (!user?.id) return;

    const { error } = await supabase.from('identity_statements').insert({
      user_id: user.id,
      statement
    });

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Update Mystara's notes
  const updateMystaraNotes = useCallback(async (updates: Partial<MystaraNotes>) => {
    if (!user?.id) return;

    const dbUpdates: Record<string, any> = {};
    if (updates.observedPatterns !== undefined) dbUpdates.observed_patterns = updates.observedPatterns;
    if (updates.lightUpTopics !== undefined) dbUpdates.light_up_topics = updates.lightUpTopics;
    if (updates.handleGently !== undefined) dbUpdates.handle_gently = updates.handleGently;
    if (updates.superpowers !== undefined) dbUpdates.superpowers = updates.superpowers;

    const { error } = await supabase
      .from('mystara_notes')
      .update(dbUpdates)
      .eq('user_id', user.id);

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    if (!user?.id) return;

    const { error } = await supabase
      .from('soul_files')
      .update({
        onboarding_completed: true,
        mirror_session_completed_at: new Date().toISOString()
      })
      .eq('user_id', user.id);

    if (error) throw error;
    await fetchSoulFile();
  }, [user?.id, fetchSoulFile]);

  return {
    soulFile,
    loading,
    error,
    updateEssence,
    updatePhase,
    addManifestation,
    updateManifestation,
    addEvidence,
    addBreakthrough,
    addIdentityStatement,
    updateMystaraNotes,
    completeOnboarding,
    refresh: fetchSoulFile
  };
}
